/**
 * Canonical lab energy range and the shared remaps every spawn / shader path
 * uses. Default stays 1.0. The old 0.2–2.0 band is the calibration interval:
 * in-band counts, speeds, and cones keep their previous formulas so existing
 * tests (which fire 1.0–2.0) stay bit-identical. Below 0.2 we shrink toward a
 * pebble-flick; above 2.0 we grow with sqrt so 5.0 is a monster, not 2.5× linear.
 *
 * Slider (UI only): geometric `E = 0.05 × 100^t`, t ∈ [0, 1], then snapped to
 * 0.05. That spends half the travel on 0.05–0.50 so the low end is hittable.
 * Probe `setEnergy` clamps + snaps; it does not go through the slider curve.
 */

export const ENERGY_MIN = 0.05;
export const ENERGY_MAX = 5.0;
export const ENERGY_DEFAULT = 1.0;
export const ENERGY_STEP = 0.05;
/** Old slider floor — in-band identity starts here. */
export const ENERGY_REF_LO = 0.2;
/** Old slider ceiling — sqrt extension starts here. */
export const ENERGY_REF_HI = 2.0;

/** High-end launch continuation: e=5 → 2 + √3 × 0.55 ≈ 2.95 (not 5). */
const LAUNCH_K = 0.55;
/** High-end radius continuation: e=5 → 1 + 0.32 ≈ 1.32. */
const RADIUS_HI_K = 0.32;
/** Pebble radius at ENERGY_MIN (sprite size, jitter, ground-current r0). */
const RADIUS_LO = 0.4;
/** Grain-count scale at ENERGY_MIN. 0.03 × ~1870 grit ≈ 56 — a handful. */
const GRAIN_COUNT_LO = 0.03;
/** Fine-count scale at ENERGY_MIN. ~0.04 × density × mass ≈ a small puff. */
const FINE_COUNT_LO = 0.04;
const GRAIN_COUNT_HI_K = 0.18;
const FINE_COUNT_HI_K = 0.22;
const JOINT_HI_K = 0.2;

export function clampEnergy(e: number): number {
  if (!Number.isFinite(e)) return ENERGY_DEFAULT;
  return Math.min(ENERGY_MAX, Math.max(ENERGY_MIN, e));
}

export function snapEnergy(e: number): number {
  const x = clampEnergy(e);
  return Math.round(x / ENERGY_STEP) * ENERGY_STEP;
}

/**
 * Geometric slider: t=0 → 0.05, t=0.5 → 0.50, t≈0.65 → 1.00, t=1 → 5.00.
 * Output is snapped to ENERGY_STEP.
 */
export function sliderToEnergy(t01: number): number {
  const t = Number.isFinite(t01) ? Math.min(1, Math.max(0, t01)) : energyToSlider(ENERGY_DEFAULT);
  const raw = ENERGY_MIN * (ENERGY_MAX / ENERGY_MIN) ** t;
  return snapEnergy(raw);
}

export function energyToSlider(e: number): number {
  const x = clampEnergy(e);
  return Math.log(x / ENERGY_MIN) / Math.log(ENERGY_MAX / ENERGY_MIN);
}

/**
 * Dynamics / launch scalar. Identity on (0, 2]; sqrt-extends above 2.
 * Used for ejection speeds, cone leftover aperture, mushroom, ground-current
 * speed. e=1 → 1, e=2 → 2, e=5 → ≈2.95.
 */
export function energyLaunch(e: number): number {
  const x = clampEnergy(e);
  if (x <= ENERGY_REF_HI) return x;
  return ENERGY_REF_HI + Math.sqrt(x - ENERGY_REF_HI) * LAUNCH_K;
}

/**
 * Spatial scale. 1.0 across the old 0.2–2.0 band; 0.40 at 0.05; ≈1.32 at 5.0.
 * Sprite size, spawn jitter, ground-current reference radius.
 */
export function energyRadius(e: number): number {
  const x = clampEnergy(e);
  if (x < ENERGY_REF_LO) {
    const t = (x - ENERGY_MIN) / (ENERGY_REF_LO - ENERGY_MIN);
    return RADIUS_LO + (1 - RADIUS_LO) * t;
  }
  if (x <= ENERGY_REF_HI) return 1;
  return 1 + RADIUS_HI_K * Math.sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}

/** Grain (grit) count multiplier. 1.0 in-band. */
export function energyCountScale(e: number): number {
  const x = clampEnergy(e);
  if (x < ENERGY_REF_LO) {
    const t = (x - ENERGY_MIN) / (ENERGY_REF_LO - ENERGY_MIN);
    return GRAIN_COUNT_LO + (1 - GRAIN_COUNT_LO) * t;
  }
  if (x <= ENERGY_REF_HI) return 1;
  return 1 + GRAIN_COUNT_HI_K * Math.sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}

/**
 * Fine-count scale. In-band this is the historical `0.55 + 0.45*(e/2)`
 * (0.775 at 1.0, 1.0 at 2.0). Outside the band it eases toward a puff / a
 * bigger cloud without changing those two anchors.
 */
export function energyFineCountScale(e: number): number {
  const x = clampEnergy(e);
  const atLo = 0.55 + 0.45 * (ENERGY_REF_LO / ENERGY_REF_HI);
  const atHi = 0.55 + 0.45 * (ENERGY_REF_HI / ENERGY_REF_HI);
  if (x < ENERGY_REF_LO) {
    const t = (x - ENERGY_MIN) / (ENERGY_REF_LO - ENERGY_MIN);
    return FINE_COUNT_LO + (atLo - FINE_COUNT_LO) * t;
  }
  if (x <= ENERGY_REF_HI) return 0.55 + 0.45 * (x / ENERGY_REF_HI);
  return atHi + FINE_COUNT_HI_K * Math.sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}

/**
 * Old cone parameter `(e - 0.2) / 1.8`. Slightly negative below 0.2 (callers
 * clamp to the low cone). Sqrt-extends above 2 to ~1.22 at 5.0.
 */
export function energyConeT(e: number): number {
  const x = clampEnergy(e);
  if (x <= ENERGY_REF_HI) return (x - ENERGY_REF_LO) / (ENERGY_REF_HI - ENERGY_REF_LO);
  return 1 + 0.22 * Math.sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}

export function mixEnergyCone(lo: number, hi: number, e: number): number {
  const t = Math.max(0, energyConeT(e));
  return lo + (hi - lo) * t;
}

/**
 * Chip hard-cap vs energy. In-band the profile cap is unchanged (64 for
 * brick). At 0.05 the cap is 1–2 so a pebble cannot dump a shard field.
 */
export function energyChipCap(e: number, hardCap: number): number {
  const cap = Math.max(0, hardCap | 0);
  if (cap <= 0) return 0;
  const x = clampEnergy(e);
  if (x >= ENERGY_REF_LO) return cap;
  const t = (x - ENERGY_MIN) / (ENERGY_REF_LO - ENERGY_MIN);
  // 1 at 0.05, full profile cap at 0.2. Floor is allowed to be 0 if cap is 0.
  return Math.max(0, Math.round(1 + (cap - 1) * t));
}

/** Joint-bleed intensity. 0 at 0.05 (no stair-step storyboard), 1 in-band. */
export function energyJointScale(e: number): number {
  const x = clampEnergy(e);
  if (x < ENERGY_REF_LO) return (x - ENERGY_MIN) / (ENERGY_REF_LO - ENERGY_MIN);
  if (x <= ENERGY_REF_HI) return 1;
  return 1 + JOINT_HI_K * Math.sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}

/**
 * GLSL twin of clamp / launch / radius. Keep the literals in lockstep with
 * the JS constants above — cloud integrate is the only GPU consumer.
 */
export const ENERGY_GLSL = /* glsl */ `
const float ENERGY_MIN = 0.05;
const float ENERGY_MAX = 5.0;
const float ENERGY_DEFAULT = 1.0;
const float ENERGY_REF_LO = 0.2;
const float ENERGY_REF_HI = 2.0;
const float ENERGY_LAUNCH_K = 0.55;
const float ENERGY_RADIUS_LO = 0.40;
const float ENERGY_RADIUS_HI_K = 0.32;

float clampEnergy(float e) {
  if (!(e == e)) return ENERGY_DEFAULT;
  return clamp(e, ENERGY_MIN, ENERGY_MAX);
}

float energyLaunch(float e) {
  float x = clampEnergy(e);
  if (x <= ENERGY_REF_HI) return x;
  return ENERGY_REF_HI + sqrt(x - ENERGY_REF_HI) * ENERGY_LAUNCH_K;
}

float energyRadius(float e) {
  float x = clampEnergy(e);
  if (x < ENERGY_REF_LO) {
    float t = (x - ENERGY_MIN) / (ENERGY_REF_LO - ENERGY_MIN);
    return ENERGY_RADIUS_LO + (1.0 - ENERGY_RADIUS_LO) * t;
  }
  if (x <= ENERGY_REF_HI) return 1.0;
  return 1.0 + ENERGY_RADIUS_HI_K * sqrt((x - ENERGY_REF_HI) / (ENERGY_MAX - ENERGY_REF_HI));
}
`;
